// A sensor, opened. Port of the Flutter installer app's doorSensor.dart, which
// is one screen with one thing on it: a big picture of the state, and the words
// for it.
//
// WHY IT EXISTS. Before this, a door sensor fell through DeviceScreen's
// `default` to the generic control fallback, which builds buttons from the
// device's OUTPUTS — and a sensor has none. The screen therefore said
// "No controllable outputs found." to somebody who had just tapped a device
// that works perfectly. There was nothing wrong except that nobody had asked it
// what it knew.
//
// Everything about the state lives in core/devices/sensorState, so this screen
// and the SensorCard on the room list can never disagree about what green means.

import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import DeviceAppBar from './components/DeviceAppBar';
import { useDeviceStatus } from '../../../../core/mqtt/useDeviceStatus';
import {
  sensorStateOf, SENSOR_ALERT, SENSOR_NORMAL,
} from '../../../../core/devices/sensorState';

const COLOURS = {
  [SENSOR_NORMAL]: '#2E7D32',
  [SENSOR_ALERT]: '#C62828',
};
const NEUTRAL = '#9E9E9E';

export default function SensorScreen({ device }) {
  const theme = useTheme();
  const c = theme.sli;
  const uno = device?.UniqueNumber;
  // Seeded from the row, then live — same as every other device screen. The
  // seed is the server's last-known value and may be old; the disc is not a
  // claim that the device is present. The liveness line under it is.
  const [status, , liveAt] = useDeviceStatus(uno, device?.Status ?? '');
  const { state, label } = sensorStateOf(status, device?.DeviceID);
  const colour = COLOURS[state] || NEUTRAL;

  const where = [device?.LocationName, device?.RoomName].filter(Boolean).join(' › ');

  return (
    <Box sx={{
      height: '100%', overflowY: 'auto', bgcolor: c.back, display: 'flex', flexDirection: 'column',
    }}
    >
      <DeviceAppBar device={device} subtitle={device?.LocationName ?? ''} />

      <Box sx={{
        flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3,
      }}
      >
        <Box sx={{ textAlign: 'center', width: '100%', maxWidth: 420 }}>
          {where && (
            <Typography sx={{ color: c.text, opacity: 0.75, fontSize: 14, mb: 3 }}>
              {where}
            </Typography>
          )}

          <Box
            sx={{
              width: 160,
              height: 160,
              mx: 'auto',
              borderRadius: '50%',
              bgcolor: colour,
              boxShadow: `0 0 0 10px ${colour}26, 0 4px 16px rgba(0,0,0,0.35)`,
            }}
          />

          <Typography sx={{
            color: c.text, fontSize: 26, fontWeight: 700, mt: 4, textTransform: 'uppercase',
          }}
          >
            {label}
          </Typography>

          <Typography sx={{ color: c.text, opacity: 0.7, fontSize: 14, mt: 1 }}>
            {device?.DeviceName || uno}
          </Typography>

          {/* `liveAt` is the only honest answer to "is it there" — see
              useDeviceStatus. Until a frame arrives, this says so rather than
              implying the disc above is current. */}
          <Typography sx={{ color: c.text, opacity: 0.55, fontSize: 12, mt: 2 }}>
            {liveAt ? 'Live' : 'Last known state'}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
